
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/components/ui/use-toast';

export interface ReadingHistoryArticle {
  id: string;
  title: string;
  author: {
    name: string;
    id: string;
  };
  date: string;
  progress: number;
  isComplete: boolean;
  coverImage?: string;
  slug?: string;
}

export const useReadingHistory = () => {
  const [readingHistory, setReadingHistory] = useState<ReadingHistoryArticle[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();

  const fetchReadingHistory = async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // First fetch the article reads
      const { data: readsData, error: readsError } = await supabase
        .from('article_reads')
        .select(`
          id,
          read_percentage,
          is_complete,
          created_at,
          updated_at,
          article_id
        `)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
      
      if (readsError) throw readsError;
      
      if (!readsData || readsData.length === 0) {
        setReadingHistory([]);
        setIsLoading(false);
        return;
      }
      
      // Get all article IDs to fetch article details
      const articleIds = readsData.map(item => item.article_id);
      
      // Fetch associated articles
      const { data: articlesData, error: articlesError } = await supabase
        .from('articles')
        .select(`
          id,
          title,
          slug,
          featured_image_url,
          author_id
        `)
        .in('id', articleIds);
      
      if (articlesError) throw articlesError;
      
      // Create a map of articles by ID for easy lookup
      const articlesMap = (articlesData || []).reduce((map, article) => {
        map[article.id] = article;
        return map;
      }, {} as Record<string, any>);
      
      // Fetch all unique author IDs
      const authorIds = Array.from(new Set(
        (articlesData || []).map(article => article.author_id).filter(Boolean)
      ));
      
      // Only fetch authors if we have any author IDs
      let authorsMap: Record<string, any> = {};
      if (authorIds.length > 0) {
        const { data: authorsData, error: authorsError } = await supabase
          .from('profiles')
          .select('id, display_name')
          .in('id', authorIds);
          
        if (authorsError) throw authorsError;
        
        // Create a map of authors by ID for easy lookup
        authorsMap = (authorsData || []).reduce((map, author) => {
          map[author.id] = author;
          return map;
        }, {} as Record<string, any>);
      }
      
      // Transform the reading history data with all the related information
      const formattedHistory = readsData.map(readItem => {
        const article = articlesMap[readItem.article_id] || {};
        const author = authorsMap[article.author_id] || {};
        
        return {
          id: article.id || 'unknown-id',
          title: article.title || 'Untitled Article',
          author: {
            id: author.id || '',
            name: author.display_name || 'Unknown Author'
          },
          date: new Date(readItem.updated_at).toLocaleDateString(),
          progress: readItem.read_percentage || 0,
          isComplete: readItem.is_complete || false,
          coverImage: article.featured_image_url || undefined,
          slug: article.slug || undefined
        };
      }).filter(item => item.id !== 'unknown-id'); // Filter out any items with missing article data
      
      setReadingHistory(formattedHistory);
    } catch (err) {
      console.error('Error fetching reading history:', err);
      setError(err instanceof Error ? err : new Error('Failed to load reading history'));
      toast({
        title: 'Error loading reading history',
        description: 'There was a problem loading your reading history.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchReadingHistory();
    } else {
      setReadingHistory([]);
      setIsLoading(false);
    }
  }, [user]);

  return {
    readingHistory,
    isLoading,
    error,
    refreshHistory: fetchReadingHistory
  };
};
